<template>
  <section class="buyers-form">
    <div class="container buyers-form__layout">

      <div class="buyers-form__editorial">
        <div class="buyers-form__label">Подбор недвижимости</div>
        <h2 class="title buyers-form__title">Подобрать недвижимость</h2>
        <p class="buyers-form__desc">Оставьте контакты — менеджер свяжется, выяснит задачу и подберёт подходящие варианты.</p>
      </div>

      <div class="buyers-form__body">

        <div v-if="success" class="buyers-form__success">
          <div class="buyers-form__success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" fill="none" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="23" stroke="#1B1918" stroke-width="1.5"/>
              <polyline points="14,24 21,31 34,17" fill="none" stroke="#1B1918" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="buyers-form__success-title">Запрос отправлен</h3>
          <p class="buyers-form__success-text">Наш менеджер свяжется с вами в ближайшее время.</p>
        </div>

        <form v-else @submit.prevent="send" novalidate class="buyers-form__form">
          <div class="buyers-form__row">
            <Input placeholder="Как вас зовут" type="text" class="input input-full" v-model="name" />
            <Input placeholder="Ваш телефон" type="tel" class="input input-full" v-model="phone" />
          </div>
          <div class="buyers-form__row">
            <Input placeholder="Ваш email (необязательно)" type="email" class="input input-full" v-model="email" />
            <select class="buyers-form__select" v-model="purpose">
              <option value="">Что ищете</option>
              <option value="Для жизни">Для жизни</option>
              <option value="Для инвестиций">Для инвестиций</option>
              <option value="Для аренды">Для аренды</option>
              <option value="Ещё не определился">Ещё не определился</option>
            </select>
          </div>
          <div class="buyers-form__field">
            <Textarea
              placeholder="Бюджет, локация, пожелания — всё это поможет подобрать вариант"
              class="input input-full"
              v-model="comment"
            />
          </div>

          <button type="submit" class="full-dark-btn buyers-form__submit" :disabled="!agreed">
            Отправить заявку
          </button>

          <Checkbox
            label="Я согласен с <a href='/privacy'>обработкой персональных данных</a> и <a href='/oferta'>публичной офертой</a>"
            :isCheck="agreed"
            name="agreement_buyers"
            @change="agreed = $event"
          />

          <p v-if="error" class="buyers-form__error">{{ error }}</p>
          <LoginHint />
        </form>

      </div>
    </div>
  </section>
</template>

<script setup>
import LoginHint from "~/components/UI/LoginHint.vue";
import Input from '~/components/UI/inputs/Input.vue'
import Textarea from '~/components/UI/inputs/Textarea.vue'
import Checkbox from '~/components/UI/btn/Checkbox.vue'
import api from '~/api/api.js'

const name    = ref('')
const phone   = ref('')
const email   = ref('')
const purpose = ref('')
const comment = ref('')
const agreed  = ref(false)
const success = ref(false)
const error   = ref('')

watch(phone, (val) => {
  let digits = val.replace(/\D/g, '')
  if (digits.startsWith('7') || digits.startsWith('8')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  let masked = '+7'
  if (digits.length > 0) masked += ' ' + digits.slice(0, 3)
  if (digits.length > 3) masked += ' ' + digits.slice(3, 6)
  if (digits.length > 6) masked += '-' + digits.slice(6, 8)
  if (digits.length > 8) masked += '-' + digits.slice(8, 10)
  if (phone.value !== masked) phone.value = masked
})

async function send() {
  error.value = ''
  if (!name.value.trim() || !phone.value.trim()) {
    error.value = 'Заполните имя и телефон.'
    return
  }
  if (!agreed.value) {
    error.value = 'Подтвердите согласие.'
    return
  }

  const fd = new FormData()
  fd.append('name', name.value)
  fd.append('phone', phone.value)
  if (email.value) fd.append('email', email.value)
  const fullComment = [purpose.value ? `Цель: ${purpose.value}` : '', comment.value].filter(Boolean).join('\n')
  if (fullComment) fd.append('message', fullComment)

  try {
    await api('/real-estate/inquiry', fd, 'POST')
    success.value = true
    name.value    = ''
    phone.value   = ''
    email.value   = ''
    purpose.value = ''
    comment.value = ''
  } catch {
    error.value = 'Произошла ошибка. Попробуйте ещё раз.'
  }
}
</script>

<style scoped lang="scss">
$dark: #1B1918;
$grey: #666;
$mobile: 48em;
$tablet: 75em;

.buyers-form {
  padding: 10rem 0 12rem;
  background: #fff;

  &__layout {
    display: grid;
    grid-template-columns: 2fr 3fr;
    gap: 8rem;
    align-items: start;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 4.8rem;
    }
  }

  &__label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(27, 25, 24, 0.35);
    margin-bottom: 2.4rem;
  }

  &__title {
    margin-bottom: 2.4rem;
  }

  &__desc {
    font-size: 1.6rem;
    line-height: 1.7;
    color: $grey;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.6rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    width: 100%;
  }

  &__select {
    width: 100%;
    height: 5rem;
    padding: 0 1.6rem;
    border: 1px solid #e0e0e0;
    border-radius: 2px;
    background: #fff;
    font-size: 1.5rem;
    color: $dark;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1.4rem center;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: $dark;
    }
  }

  &__submit {
    align-self: flex-start;
    margin-top: 0.4rem;
  }

  &__error {
    font-size: 1.4rem;
    color: #c62828;
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.2rem;
    padding: 3.2rem 0;

    &-icon svg { display: block; }

    &-title {
      font-size: 2.4rem;
      font-weight: 500;
      color: $dark;
      line-height: 1.3;
    }

    &-text {
      font-size: 1.6rem;
      color: #888;
    }
  }
}
</style>
